import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import "./SchoolDetail.css";
import TopImage from "../../common/TopImage";
import { Images } from "../../../../config/images";
import TeacherTable from "./TeacherTable";
import SubSchoolStudentTable from "./SubSchoolStudentTable";
import DashboardStats from "../../../common/Dashboard/DashboardStats";
import RefreshButtonTable from "../../../common/RefreshButtonTable";
import StudentPerformance from "./StudentPerformance";
import TeacherPerformance from "./TeacherPerformance";
import BackButton from "../../../common/BackButton";
import { useGetSubSchoolReportById } from "../../../../hooks/organization/report/useGetSubSchoolReportById";
import { useGetSubSchoolStudentsList } from "../../../../hooks/organization/report/useGetSubSchoolStudentsList";
import { ITEMS_PER_PAGE } from "../../../../constant/constant";
import DashboardStatsSkeleton from "../../../common/Dashboard/DashboardStatsSkeleton";
import toast from "react-hot-toast";

/**
 * SchoolDetail component displays detailed information about a school
 * including school info, stats, and teacher/student lists
 */
const SchoolDetail = () => {
  const navigate = useNavigate();
  const { id: schoolId } = useParams();
  const [activeTab, setActiveTab] = useState("teachers");

  // Pagination state for teachers
  const [teacherPage, setTeacherPage] = useState(1);

  // Pagination state for students
  const [studentPage, setStudentPage] = useState(1);

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  // Teacher report hook
  const {
    loading: teacherLoading,
    error: teacherError,
    reportData,
    getSubSchoolReportById,
    metaData: teacherMetaData,
  } = useGetSubSchoolReportById();

  // Student list hook
  const {
    loading: studentLoading,
    error: studentError,
    studentsData,
    getSubSchoolStudentsList,
    metaData: studentMetaData,
  } = useGetSubSchoolStudentsList();

  // Fetch teacher report data on initial load and when page changes
  useEffect(() => {
    if (schoolId) {
      getSubSchoolReportById(schoolId, teacherPage, ITEMS_PER_PAGE);
    }
  }, [schoolId, teacherPage]);

  // Fetch student data when switching to students tab or page changes
  useEffect(() => {
    if (schoolId && activeTab === "students") {
      getSubSchoolStudentsList(schoolId, studentPage, ITEMS_PER_PAGE);
    }
  }, [schoolId, activeTab, studentPage]);

  const handleTeacherRefresh = () => {
    getSubSchoolReportById(schoolId, teacherPage, ITEMS_PER_PAGE);
  };

  const handleStudentRefresh = () => {
    getSubSchoolStudentsList(schoolId, studentPage, ITEMS_PER_PAGE);
  };

  // Check if initial load for school info
  const isSchoolInfoLoading = teacherLoading && reportData === undefined;

  // School info from API
  const schoolData = {
    name: reportData?.school?.name || "-",
    email: reportData?.school?.email || "-",
    image: reportData?.school?.image_url || "/placeholder-school.png",
    licensesUsed: reportData?.school?.licenses_used ?? 0,
    licensesAssigned: reportData?.school?.licenses_assigned ?? 0,
    totalTeachers: reportData?.stats?.total_teachers ?? 0,
    subdomain: reportData?.school?.subdomain || "-",
  };

  // Format license display
  const licenseDisplay = `${schoolData.licensesUsed}/${schoolData.licensesAssigned}`;

  // School Info Card Skeleton
  const SchoolInfoSkeleton = () => (
    <div className="school-info-card">
      <div className="school-info-left">
        <div className="school-info-header">
          <div
            className="skeleton-box"
            style={{ width: 80, height: 80, borderRadius: 10 }}
          ></div>
          <div className="school-details">
            <div
              className="skeleton-box"
              style={{ width: 180, height: 24, marginBottom: 8 }}
            ></div>
            <div
              className="skeleton-box"
              style={{ width: 220, height: 16 }}
            ></div>
          </div>
        </div>
        <div
          className="skeleton-box mt-1"
          style={{ width: 280, height: 32, borderRadius: 8 }}
        ></div>
      </div>
      <div className="school-info-right">
        <div className="school-info-stats-container">
          <div className="school-stat">
            <div
              className="skeleton-box"
              style={{ width: 60, height: 14, marginBottom: 8 }}
            ></div>
            <div className="skeleton-box" style={{ width: 50, height: 24 }}></div>
          </div>
          <div className="school-stat">
            <div
              className="skeleton-box"
              style={{ width: 60, height: 14, marginBottom: 8 }}
            ></div>
            <div className="skeleton-box" style={{ width: 30, height: 24 }}></div>
          </div>
        </div>
        <div
          className="skeleton-box"
          style={{ width: 120, height: 40, borderRadius: 20 }}
        ></div>
      </div>
    </div>
  );

  // Stats data from API for Teachers tab
  const teacherStats = [
    {
      label: "Total Teachers",
      value:
        teacherLoading && !reportData
          ? "..."
          : (reportData?.stats?.total_teachers ?? 0),
      icon: "fa-chalkboard-user",
      colorClass: "dashboard-stat-card-purple",
    },
    {
      label: "Avg. Student Passing Score",
      value:
        teacherLoading && !reportData
          ? "..."
          : `${reportData?.stats?.avg_student_passing_score ?? 0}%`,
      icon: "fa-chart-line",
      colorClass: "dashboard-stat-card-green",
    },
    {
      label: "Highest Performing Teacher",
      value:
        teacherLoading && !reportData
          ? "..."
          : reportData?.stats?.highest_performing_teacher?.name || "N/A",
      subValue:
        teacherLoading && !reportData
          ? ""
          : `${reportData?.stats?.highest_performing_teacher?.avg_progress ?? 0}% avg progress`,
      icon: "fa-trophy",
      colorClass: "dashboard-stat-card-peach",
    },
    {
      label: "Lowest Performing Teacher",
      value:
        teacherLoading && !reportData
          ? "..."
          : reportData?.stats?.lowest_performing_teacher?.name || "N/A",
      subValue:
        teacherLoading && !reportData
          ? ""
          : `${reportData?.stats?.lowest_performing_teacher?.avg_progress ?? 0}% avg progress`,
      icon: "fa-chart-simple",
      colorClass: "dashboard-stat-card-pastel-blue",
    },
  ];

  // Stats data from API for Students tab
  const studentStats = [
    {
      label: "Total Students",
      value:
        studentLoading && !studentsData
          ? "..."
          : (studentsData?.students?.total ?? 0),
      icon: "fa-graduation-cap",
      colorClass: "dashboard-stat-card-purple",
    },
    {
      label: "Overall Progress",
      value:
        studentLoading && !studentsData
          ? "..."
          : `${studentsData?.avg_overall_progress ?? 0}%`,
      icon: "fa-chart-line",
      colorClass: "dashboard-stat-card-green",
    },
    {
      label: "Avg. Quiz Score",
      value:
        studentLoading && !studentsData
          ? "..."
          : (studentsData?.avg_quiz_score ?? 0),
      icon: "fa-file-check",
      colorClass: "dashboard-stat-card-peach",
    },
    {
      label: "Total Quests Assigned",
      value:
        studentLoading && !studentsData
          ? "..."
          : (studentsData?.total_quests_assigned ?? 0),
      icon: "fa-scroll",
      colorClass: "dashboard-stat-card-pastel-blue",
    },
  ];

  // Teachers data from API
  const teachers = reportData?.teachers?.data || [];

  // Students data from API
  const students = studentsData?.students?.data || [];

  // Current loading and refresh handlers based on active tab
  const isLoading = activeTab === "teachers" ? teacherLoading : studentLoading;
  const handleRefresh =
    activeTab === "teachers" ? handleTeacherRefresh : handleStudentRefresh;
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);

    toast.success("Subdomain copied to clipboard", {
      icon: "📋",
    });
  };



  return (
    <div className="school-performance-cnt">
      <BackButton />
      {/* Top Banner */}
      <TopImage text="School Performance Overview" imageSrc={Images.TopImage} />

      {/* School Info Card */}
      {isSchoolInfoLoading ? (
        <SchoolInfoSkeleton />
      ) : (
        <div className="school-info-card">
          <div className="school-info-left">
            <div className="school-info-header">
              <img
                src={schoolData.image || "/placeholder-school.png"}
                alt={schoolData.name}
                className="school-avatar"
              />
              <div className="school-details">
                <h3>{schoolData.name}</h3>
                <p className="mb-0">
                  <strong>Email:</strong> {schoolData.email}
                </p>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 mt-1 flex-wrap school-link-container">
              <div 
                className="subdomainText subdomain-scroll d-flex align-items-center" 
                style={{ 
                  fontSize: "14px", 
                  color: "#1e1d38", 
                  backgroundColor: "#f8f9fa", 
                  padding: "6px 12px", 
                  borderRadius: "8px", 
                  border: "1px solid #e9ecef",
                  maxWidth: "100%",
                  overflowX: "auto",
                  whiteSpace: "nowrap"
                }}
              >
                <i className="fa-solid fa-link me-2 text-muted"></i>
                <span>https://<span className="fw-semibold text-primary">{schoolData.subdomain}</span>.{process.env.REACT_APP_MAIN_DOMAIN}</span>
              </div>

              <button
                className="btn btn-sm btn-light border d-flex align-items-center shadow-sm flex-shrink-0"
                style={{ 
                  padding: "6px 14px", 
                  fontSize: "13px",
                  borderRadius: "8px",
                  fontWeight: "500"
                }}
                onClick={() =>
                  handleCopy(
                    `https://${schoolData.subdomain}.${process.env.REACT_APP_MAIN_DOMAIN}`,
                  )
                }
              >
                <i className="fa-regular fa-copy me-2" style={{ color: "#0072bc" }}></i>
                Copy Link
              </button>
            </div>
          </div>
          <div className="school-info-right">
            <div className="school-info-stats-container">
              <div className="school-stat">
                <div className="school-stat-label">Licenses</div>
                <div className="school-stat-value">{licenseDisplay}</div>
              </div>
              <div className="school-stat">
                <div className="school-stat-label">Teachers</div>
                <div className="school-stat-value">
                  {String(schoolData.totalTeachers).padStart(2, "0")}
                </div>
              </div>
            </div>
            <Button
              className="view-profile-btn"
              onClick={() => navigate(`/organization/school/edit/${schoolId}`)}
            >
              View Profile
            </Button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div>
        <div className="tabs-container">
          <button
            className={`tab-btn ${activeTab === "teachers" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("teachers");
              setSelectedStudent(null);
              setSelectedTeacher(null);
            }}
          >
            Teachers
          </button>
          <button
            className={`tab-btn ${activeTab === "students" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("students");
              setSelectedTeacher(null);
            }}
          >
            Students
          </button>
        </div>
      </div>

      <RefreshButtonTable handleRefresh={handleRefresh} loading={isLoading} />

      {/* Performance Stats */}
      {activeTab === "teachers" ? (
        <>
          {selectedTeacher ? (
            <TeacherPerformance
              teacher={selectedTeacher}
              onBack={() => setSelectedTeacher(null)}
            />
          ) : (
            <>
              {teacherLoading && reportData === undefined ? (
                <DashboardStatsSkeleton count={4} />
              ) : (
                <DashboardStats stats={teacherStats} />
              )}
              <TeacherTable
                teachers={teachers}
                loading={teacherLoading}
                error={teacherError}
                metaData={teacherMetaData}
                currentPage={teacherPage}
                setCurrentPage={setTeacherPage}
                handleRefresh={handleTeacherRefresh}
                onTeacherClick={setSelectedTeacher}
                isInitialLoad={reportData === undefined}
              />
            </>
          )}
        </>
      ) : (
        <>
          {selectedStudent ? (
            <StudentPerformance
              student={selectedStudent}
              onBack={() => setSelectedStudent(null)}
            />
          ) : (
            <>
              {studentLoading && studentsData === undefined ? (
                <DashboardStatsSkeleton count={4} />
              ) : (
                <DashboardStats stats={studentStats} />
              )}
              <SubSchoolStudentTable
                students={students}
                loading={studentLoading}
                error={studentError}
                metaData={studentMetaData}
                currentPage={studentPage}
                setCurrentPage={setStudentPage}
                handleRefresh={handleStudentRefresh}
                onStudentClick={setSelectedStudent}
                isInitialLoad={studentsData === undefined}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SchoolDetail;
